import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import ListItemIcon from '@mui/material/ListItemIcon';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Chip from '@mui/material/Chip';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PrintIcon from '@mui/icons-material/Print';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import WarningIcon from '@mui/icons-material/Warning';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import { getMatch, updateMatch, getShooters, updateShooter } from '../../api';
import { getFlagSrc } from '../../utils/scoring';
import Snackbar from '../../components/Snackbar';

const emptySquad = () => ({
  id: uuid(),
  title: '',
  maxShooters: 20,
  description: '',
  firstStage: 1,
});

export default function Squads() {
  const { matchId } = useParams();
  const { t } = useTranslation();
  const [match, setMatch] = useState(null);
  const [squads, setSquads] = useState([]);
  const [shooters, setShooters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(-1);
  const [current, setCurrent] = useState(emptySquad());
  const [assignOpen, setAssignOpen] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [dragState, setDragState] = useState(null); // { squadId, shooterIdx }
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [matchRes, shootersRes] = await Promise.all([
        getMatch(matchId),
        getShooters(matchId).catch(() => ({ data: [] })),
      ]);
      setMatch(matchRes.data);
      setSquads(matchRes.data.squads || []);
      setShooters(Array.isArray(shootersRes.data) ? shootersRes.data : []);
    } catch (err) {
      setSnack({ open: true, message: t('squads.loadFailed'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [matchId, t]);

  useEffect(() => { loadData(); }, [loadData]);

  const getOrderedMembers = (squad) => {
    const memberIds = new Set(shooters.filter((s) => s.squad === squad.id).map((s) => s.id));
    // Start with ordered IDs that are still members, then append any members not in the order
    const order = squad.order || [];
    const orderedIds = order.filter((id) => memberIds.has(id));
    const unordered = [...memberIds].filter((id) => !order.includes(id));
    return [...orderedIds, ...unordered].map((id) => shooters.find((s) => s.id === id)).filter(Boolean);
  };

  const handleAdd = () => {
    setEditIndex(-1);
    setCurrent(emptySquad());
    setDialogOpen(true);
  };

  const handleEdit = (idx) => {
    setEditIndex(idx);
    setCurrent({ ...squads[idx] });
    setDialogOpen(true);
  };

  const saveSquads = async (newSquads) => {
    setSquads(newSquads);
    try {
      await updateMatch(matchId, { squads: newSquads });
    } catch (err) {
      setSnack({ open: true, message: err.response?.data?.error || t('common.saveError'), severity: 'error' });
      await loadData();
    }
  };

  const handleDialogSave = () => {
    const newSquads = [...squads];
    if (editIndex >= 0) {
      newSquads[editIndex] = current;
    } else {
      newSquads.push(current);
    }
    saveSquads(newSquads);
    setDialogOpen(false);
  };

  const handleDelete = (idx) => {
    saveSquads(squads.filter((_, i) => i !== idx));
    setConfirmDelete(null);
  };

  const handleAssignShooter = async (squadId, shooterId) => {
    const squad = squads.find((sq) => sq.id === squadId);
    if (squad && squad.maxShooters) {
      const currentCount = shooters.filter((s) => s.squad === squadId).length;
      if (currentCount >= squad.maxShooters) {
        setSnack({ open: true, message: t('squads.squadFull', 'This squad is full'), severity: 'warning' });
        return;
      }
    }
    try {
      await updateShooter(matchId, shooterId, { squad: squadId });
      setShooters((prev) => prev.map((s) => s.id === shooterId ? { ...s, squad: squadId } : s));
      const newSquads = squads.map((sq) => sq.id === squadId ? { ...sq, order: [...(sq.order || []), shooterId] } : sq);
      await saveSquads(newSquads);
      setSnack({ open: true, message: t('squads.shooterAssigned'), severity: 'success' });
    } catch (err) {
      setSnack({ open: true, message: t('squads.assignFailed'), severity: 'error' });
    }
  };

  const handleUnassignShooter = async (squadId, shooterId) => {
    try {
      await updateShooter(matchId, shooterId, { squad: null });
      setShooters((prev) => prev.map((s) => s.id === shooterId ? { ...s, squad: null } : s));
      const newSquads = squads.map((sq) => sq.id === squadId ? { ...sq, order: (sq.order || []).filter((id) => id !== shooterId) } : sq);
      await saveSquads(newSquads);
      setSnack({ open: true, message: t('squads.shooterUnassigned'), severity: 'success' });
    } catch (err) {
      setSnack({ open: true, message: t('squads.unassignFailed'), severity: 'error' });
    }
  };

  const handleShooterDragStart = (squadId, shooterIdx) => {
    setDragState({ squadId, shooterIdx });
  };

  const handleShooterDragOver = (e) => e.preventDefault();

  const handleShooterDrop = (squadId, dropIdx) => {
    if (!dragState || dragState.squadId !== squadId || dragState.shooterIdx === dropIdx) {
      setDragState(null);
      return;
    }
    const squadIdx = squads.findIndex((sq) => sq.id === squadId);
    if (squadIdx < 0) { setDragState(null); return; }
    const newSquads = [...squads];
    const squad = { ...newSquads[squadIdx] };
    const members = getOrderedMembers(squad);
    const order = members.map((s) => s.id);
    const [moved] = order.splice(dragState.shooterIdx, 1);
    order.splice(dropIdx, 0, moved);
    squad.order = order;
    newSquads[squadIdx] = squad;
    saveSquads(newSquads);
    setDragState(null);
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }

  const unassignedShooters = shooters.filter((s) => !s.squad || !squads.find((sq) => sq.id === s.squad));

  return (
    <Container maxWidth="lg" sx={{ py: 3, '@media print': { maxWidth: '100%', p: 0 } }}>
      <Box className="noprint" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">{t('matches.squads')}</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrint}>{t('squads.print')}</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>{t('squads.addSquad')}</Button>
        </Box>
      </Box>

      <Typography className="onlyprint" variant="h4" sx={{ mb: 2, textAlign: 'center', fontWeight: 'bold' }}>
        {match?.title} — {t('matches.squads')}
      </Typography>

      <Grid container spacing={2} sx={{ '@media print': { display: 'flex', flexWrap: 'wrap' } }}>
        {squads.map((squad, idx) => {
          const members = getOrderedMembers(squad);
          return (
            <Grid key={squad.id} size={{ xs: 12, sm: 6, md: 4 }} sx={{ '@media print': { breakInside: 'avoid', pageBreakInside: 'avoid' } }}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', '@media print': { boxShadow: 'none', border: '1px solid #ccc' } }}>
                <CardContent sx={{ flexGrow: 1, '@media print': { p: 1, '&:last-child': { pb: 1 } } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6">{squad.title}</Typography>
                    {squad.ro && <Chip label="RO" size="small" color="primary" sx={{ fontWeight: 800 }} />}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('squads.memberCount', { current: members.length, max: squad.maxShooters })} | {t('squads.firstStage')}: {squad.firstStage || 1}
                  </Typography>
                  {squad.description && (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>{squad.description}</Typography>
                  )}
                  <Divider sx={{ my: 1 }} />
                  <List dense sx={{ maxHeight: 300, overflow: 'auto', '@media print': { maxHeight: 'none', overflow: 'visible' } }}>
                    {members.map((shooter, sIdx) => (
                      <ListItem
                        key={shooter.id}
                        disablePadding
                        draggable
                        onDragStart={() => handleShooterDragStart(squad.id, sIdx)}
                        onDragOver={handleShooterDragOver}
                        onDrop={() => handleShooterDrop(squad.id, sIdx)}
                        sx={{ cursor: 'grab', '&:hover': { bgcolor: 'action.hover' }, borderRadius: 1, pr: 5, '@media print': { cursor: 'default', pr: 0 } }}
                        secondaryAction={
                          <Tooltip title={t('squads.unassign')}>
                            <IconButton className="noprint" edge="end" size="small" onClick={() => handleUnassignShooter(squad.id, shooter.id)}>
                              <PersonRemoveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        }
                      >
                        <ListItemIcon className="noprint" sx={{ minWidth: 28 }}>
                          <DragIndicatorIcon fontSize="small" color="action" />
                        </ListItemIcon>
                        <ListItemText
                          primary={shooter.name}
                          secondary={(() => {
                            const parts = (shooter.publicId || '').split('|');
                            const cc = parts.length > 1 ? parts[0] : null;
                            const num = parts.length > 1 ? parts.slice(1).join('|') : shooter.publicId;
                            const flag = cc ? getFlagSrc({ countryCode: cc, size: 16 }) : null;
                            return (
                              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                {flag && <img src={flag} alt={cc} style={{ width: 16, height: 12 }} />}
                                {num || '?'} | {t(`divisions.${(shooter.division || '').replace(/-([a-z])/g, (_, c) => c.toUpperCase())}`, shooter.division)}{shooter.category && shooter.category !== 'regular' ? ` | ${t(`categories.${(shooter.category || '').replace(/-([a-z])/g, (_, c) => c.toUpperCase())}`, shooter.category)}` : ''}
                              </Box>
                            );
                          })()}
                          sx={{ '@media print': { my: 0 } }}
                        />
                      </ListItem>
                    ))}
                    {members.length === 0 && (
                      <ListItem>
                        <ListItemIcon sx={{ minWidth: 28 }}><WarningIcon fontSize="small" sx={{ color: 'warning.main' }} /></ListItemIcon>
                        <ListItemText primary={t('squads.noShooters')} />
                      </ListItem>
                    )}
                  </List>
                </CardContent>
                <CardActions className="noprint">
                  <Tooltip title={t('common.edit')}><IconButton size="small" color="primary" onClick={() => handleEdit(idx)}><EditIcon /></IconButton></Tooltip>
                  <Tooltip title={t('squads.assign')}><IconButton size="small" color="primary" onClick={() => setAssignOpen(squad.id)}><PersonAddIcon /></IconButton></Tooltip>
                  <Tooltip title={t('common.delete')}><IconButton size="small" color="error" onClick={() => setConfirmDelete(idx)} disabled={members.length > 0}><DeleteIcon /></IconButton></Tooltip>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Squad Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editIndex >= 0 ? t('squads.editSquad') : t('squads.createSquad')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label={t('squads.title')} value={current.title} onChange={(e) => setCurrent({ ...current, title: e.target.value })} fullWidth />
            <TextField label={t('squads.maxShooters')} type="number" value={current.maxShooters} onChange={(e) => setCurrent({ ...current, maxShooters: parseInt(e.target.value, 10) || 0 })} fullWidth />
            <TextField label={t('squads.description')} value={current.description || ''} onChange={(e) => setCurrent({ ...current, description: e.target.value })} fullWidth multiline rows={2} />
            <TextField label={t('squads.firstStage')} type="number" value={current.firstStage || 1} onChange={(e) => setCurrent({ ...current, firstStage: parseInt(e.target.value, 10) || 1 })} fullWidth />
            <FormControlLabel
              control={<Switch checked={!!current.ro} onChange={(e) => setCurrent({ ...current, ro: e.target.checked })} />}
              label={t('squads.roOnly')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleDialogSave} variant="contained" disabled={!current.title}>{t('common.save')}</Button>
        </DialogActions>
      </Dialog>

      {/* Assign Shooter Dialog */}
      <Dialog open={assignOpen !== null} onClose={() => setAssignOpen(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('squads.assignShooter')}</DialogTitle>
        <DialogContent>
          {(() => {
            const assignSquad = squads.find((sq) => sq.id === assignOpen);
            const currentCount = assignSquad ? shooters.filter((s) => s.squad === assignOpen).length : 0;
            const isFull = assignSquad && assignSquad.maxShooters && currentCount >= assignSquad.maxShooters;
            return (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('squads.unassignedCount', { count: unassignedShooters.length })}
                  {assignSquad?.maxShooters ? ` | ${currentCount}/${assignSquad.maxShooters}` : ''}
                </Typography>
                {isFull && (
                  <Alert severity="warning" sx={{ mb: 2 }}>{t('squads.squadFull', 'This squad is full')}</Alert>
                )}
              </>
            );
          })()}
          <List dense sx={{ maxHeight: 400, overflow: 'auto' }}>
            {unassignedShooters.map((shooter) => (
              <ListItem
                key={shooter.id}
                component="div"
                onClick={() => { handleAssignShooter(assignOpen, shooter.id); setAssignOpen(null); }}
                sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, borderRadius: 1 }}
              >
                <ListItemText primary={shooter.name} secondary={`#${shooter.publicId || '?'} | ${shooter.division || ''}`} />
              </ListItem>
            ))}
            {unassignedShooters.length === 0 && (
              <ListItem><ListItemText primary={t('squads.allAssigned')} /></ListItem>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignOpen(null)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={confirmDelete !== null} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>{t('squads.deleteSquad')}</DialogTitle>
        <DialogContent>
          <Typography>{t('squads.deleteSquadConfirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>{t('common.cancel')}</Button>
          <Button onClick={() => handleDelete(confirmDelete)} color="error" variant="contained">{t('common.delete')}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} message={snack.message} severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })} />
    </Container>
  );
}
